/**
 * Reusable GLSL signed-distance-field (SDF) snippets, shared between the
 * game's raymarched scenes. Exported as strings so they can be concatenated
 * into Babylon.js fragment shaders.
 *
 * Conventions: distance functions return the signed distance to a surface
 * (negative inside). Coordinates are world-space unless a primitive folds
 * space itself. These are the classic Inigo Quilez primitives, trimmed to
 * what Milestone 1 needs (a sphere) plus the building blocks M2 will use.
 */

/** Primitive distance functions. */
export const SDF_PRIMITIVES = /* glsl */ `
float sdSphere(vec3 p, float r) {
  return length(p) - r;
}

float sdBox(vec3 p, vec3 b) {
  vec3 q = abs(p) - b;
  return length(max(q, 0.0)) + min(max(q.x, max(q.y, q.z)), 0.0);
}

// Infinite ground plane at y = h, used for the Tron "grid floor".
float sdPlane(vec3 p, float h) {
  return p.y - h;
}
`;

/** Boolean / blending operators for combining SDFs. */
export const SDF_OPERATORS = /* glsl */ `
float opUnion(float d1, float d2) { return min(d1, d2); }
float opSubtract(float d1, float d2) { return max(-d1, d2); }
float opIntersect(float d1, float d2) { return max(d1, d2); }

// Smooth union — gives the fluid, blobby look that suits a quantum scene.
float opSmoothUnion(float d1, float d2, float k) {
  float h = clamp(0.5 + 0.5 * (d2 - d1) / k, 0.0, 1.0);
  return mix(d2, d1, h) - k * h * (1.0 - h);
}
`;

/** A neon "Tron grid" colour helper, driven by world XZ position. */
export const SDF_TRON_GRID = /* glsl */ `
// Returns a 0..1 mask that is bright along grid lines, dark in cells.
float tronGrid(vec2 xz, float cell, float lineWidth) {
  vec2 g = abs(fract(xz / cell - 0.5) - 0.5) / fwidth(xz / cell);
  float line = min(g.x, g.y);
  return 1.0 - smoothstep(0.0, lineWidth, line);
}
`;

/** Everything, in dependency order, ready to prepend to a scene's body. */
export const SDF_LIB = `${SDF_PRIMITIVES}\n${SDF_OPERATORS}\n${SDF_TRON_GRID}`;
